import { Link } from "react-router-dom";
import { Megaphone, AlertCircle, Info, ArrowRight, X } from "lucide-react";
import { useState } from "react";
import { useSeasonalBanner } from "../lib/homepageData";
import BannerShell from "./BannerShell";

/**
 * Iter-189 Sprint 4 — Seasonal Banner.
 *
 * Renders ABOVE the Hero when the admin has enabled an active banner
 * in `/admin?tab=homepage-cms`. Auto-hides:
 *   - if `enabled = false` in the admin override
 *   - outside the `starts_at` / `ends_at` window
 *   - if the operator has not configured any text
 *   - after the visitor clicks the dismiss "X" (per-tab dismiss via
 *     sessionStorage so it doesn't bounce back on every nav)
 *
 * Three tones supported (admin-selectable):
 *   - info   (slate, neutral system message)
 *   - promo  (emerald, conversion-oriented)
 *   - alert  (amber, urgent)
 */
const TONE_STYLES = {
  info: {
    bg: "bg-slate-950 text-white",
    icon: Info,
    iconCls: "text-sky-400",
    linkCls: "text-sky-300 hover:text-sky-100",
  },
  promo: {
    bg: "bg-[#0A2540] text-white",
    icon: Megaphone,
    iconCls: "text-[#10B981]",
    linkCls: "text-[#34D399] hover:text-white",
  },
  alert: {
    bg: "bg-amber-500 text-slate-950",
    icon: AlertCircle,
    iconCls: "text-slate-950",
    linkCls: "text-slate-950 hover:underline font-black",
  },
};

const DISMISS_KEY_PREFIX = "carsavr.banner_dismissed.";

function bannerDismissKey(banner) {
  // Per-banner dismissal — operators can ship a new banner and old
  // dismissals don't apply. Cache key bundles the text so a copy
  // edit also re-shows the banner to previous dismissers.
  return DISMISS_KEY_PREFIX + (banner.text || "").slice(0, 60).replace(/\s/g, "_");
}

export default function SeasonalBanner() {
  const banner = useSeasonalBanner();
  const [dismissed, setDismissed] = useState(() => {
    if (!banner.active) return false;
    try {
      return sessionStorage.getItem(bannerDismissKey(banner)) === "1";
    } catch {
      return false;
    }
  });

  if (!banner.active || dismissed) return null;

  const tone = TONE_STYLES[banner.tone] || TONE_STYLES.promo;
  const Icon = tone.icon;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(bannerDismissKey(banner), "1");
    } catch {
      /* swallow */
    }
  };

  return (
    <BannerShell
      data-testid="seasonal-banner"
      className={`${tone.bg} text-xs sm:text-sm`}
      role="region"
      ariaLabel="Site-wide announcement"
      innerClassName="py-2 flex items-center gap-3"
    >
        <Icon className={`w-3.5 h-3.5 shrink-0 ${tone.iconCls}`} strokeWidth={2.5} />
        <p className="flex-1 leading-snug" data-testid="seasonal-banner-text">
          {banner.text}
        </p>
        {banner.cta_url && banner.cta_text && (
          <Link
            to={banner.cta_url}
            data-testid="seasonal-banner-cta"
            className={`shrink-0 inline-flex items-center gap-1 font-bold uppercase tracking-[0.16em] text-[10px] ${tone.linkCls}`}
          >
            {banner.cta_text}
            <ArrowRight className="w-3 h-3" />
          </Link>
        )}
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="seasonal-banner-dismiss"
          aria-label="Dismiss announcement"
          className={`shrink-0 p-1 -mr-1 opacity-60 hover:opacity-100 transition-opacity ${tone.iconCls}`}
        >
          <X className="w-3.5 h-3.5" strokeWidth={2.5} />
        </button>
    </BannerShell>
  );
}
